// src/components/navigation/SidebarNav.js
//
// Phase 3 — Navigation Architecture: Step 7 reusable navigation component.
//
// Registry-driven list renderer used by LeftNav (desktop sidebar) to render
// any NavigationItem[] (Global, Personal, Church, or Circle navigation
// trees from NavigationRegistry). This is the single place that knows how
// to render a NavigationItem as a list entry — no page or section may
// define its own item rendering.

import React from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Badge,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { getNavIcon } from "./iconMap";

const ACCENT = "#2D6A4F";
const ACCENT_LIGHT = "#E8F4EE";

export function SidebarSectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "1px",
        textTransform: "uppercase",
        color: "#A09892",
        px: 2.5,
        pt: 1.5,
        pb: 0.5,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {children}
    </Typography>
  );
}

function isItemActive(item, location) {
  if (item.matchRoutes) {
    return item.matchRoutes.some((route) => location.pathname.startsWith(route));
  }
  if (item.matchSearch) {
    return location.pathname === "/" && location.search.includes(item.matchSearch);
  }
  if (item.exact) {
    if (item.route === "/" && location.search.includes("filter=answered")) {
      return false;
    }
    return location.pathname === item.route;
  }
  const routePath = item.route.split("?")[0];
  return location.pathname.startsWith(routePath) && routePath !== "/";
}

/**
 * @param {object} props
 * @param {import("../../navigation/NavigationRegistry").NavigationItem[]} props.items
 * @param {Record<string, number>} [props.badgeCounts] - badgeKey -> count
 * @param {(action: string) => void} [props.onAction] - handles items whose
 *   `action` field requires UI behavior outside of routing (e.g. "openSearch").
 */
export default function SidebarNav({ items, badgeCounts = {}, onAction }) {
  const location = useLocation();

  return (
    <List dense disablePadding>
      {items.map((item) => {
        const Icon = getNavIcon(item.icon);
        const badge = item.badgeKey ? badgeCounts[item.badgeKey] || 0 : 0;
        const active = isItemActive(item, location);

        if (item.comingSoon) {
          return (
            <ListItemButton
              key={item.id}
              disabled
              component="div"
              title="Coming in a future phase"
              aria-label={`${item.label}, coming soon`}
              sx={{ borderRadius: 0, mb: 0, minHeight: 44, py: "7px", px: 2.5, opacity: 0.45 }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: "#A09892" }}>
                <Icon sx={{ fontSize: 16 }} />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: "13px",
                  color: "#A09892",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              />
            </ListItemButton>
          );
        }

        return (
          <ListItemButton
            key={item.id}
            component={item.action ? "div" : Link}
            to={item.action ? undefined : item.route}
            onClick={item.action ? () => onAction?.(item.action) : undefined}
            aria-label={badge > 0 ? `${item.label}, ${badge} unread` : item.label}
            aria-current={active ? "page" : undefined}
            sx={{
              borderRadius: 0,
              mb: 0,
              minHeight: 44,
              py: "7px",
              px: 2.5,
              backgroundColor: active ? ACCENT_LIGHT : "transparent",
              borderRight: active ? `2px solid ${ACCENT}` : "2px solid transparent",
              "&:hover": { backgroundColor: "rgba(45,106,79,0.05)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: active ? ACCENT : "#6B6460" }}>
              <Badge badgeContent={badge} color="error" max={99}>
                <Icon sx={{ fontSize: 16 }} />
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: "13px",
                fontWeight: active ? 500 : 400,
                color: active ? ACCENT : "#6B6460",
                fontFamily: "'DM Sans', sans-serif",
              }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );
}
